/*
  NOTE: Moved some code here since Hydrogen had runtime complaint about some of these exports
  from a .client.tsx used in .server.tsx
*/

import {filterRanges, PAGINATION_PARAMS} from '../constants';
import {parseSearchTokens, stringifySearchTokens} from './searchTokens';

export interface FilterRange {
  min: number;
  max: number;
}

export interface ShopFilterParamsType {
  searchTokens: Array<string>;
  priceTags: Array<string>;
  brands: Array<string>;
  types: Array<string>;
  countries: Array<string>;
  categories: Array<string>;
  ratings: Array<string>;
  partnerStores: Array<string>;
  styles: Array<string>;
  age: FilterRange;
  abv: FilterRange;
  inStock: string;
  list: string;
  inTwlShop: string;
}

export function getFilterParamsFromHref(
  href: string,
  ignoreFilters?: string[],
): {
  filters: ShopFilterParamsType;
  active: boolean;
} {
  const {searchParams} = new URL(href);

  let active = false;

  function getIsActive(param: string) {
    return (
      !(ignoreFilters && ignoreFilters.includes(param)) &&
      searchParams.has(param)
    );
  }

  function getRangeParam(name: string, min: number, max: number) {
    const minParam = `${name}Min`;
    const maxParam = `${name}Max`;

    active ||= getIsActive(minParam) || getIsActive(maxParam);

    const minVal = getInt(searchParams.get(minParam), min, min, max);
    const maxVal = getInt(searchParams.get(maxParam), max, minVal, max);
    return {min: minVal, max: maxVal};
  }

  function getParam(name: string, all?: Exclude<boolean, false>): Array<string>;
  function getParam(name: string, all: Exclude<boolean, true>): string;
  function getParam(name: string, all = true): string | Array<string> {
    active ||= getIsActive(name);
    if (all) {
      return searchParams.getAll(name);
    }
    return searchParams.get(name);
  }

  return {
    filters: {
      list: getParam('list', false),
      inStock: getParam('inStock', false) ?? '',
      priceTags: getParam('priceTags'),
      brands: getParam('brands'),
      types: getParam('types'),
      countries: getParam('countries'),
      categories: getParam('categories'),
      ratings: getParam('ratings'),
      partnerStores: getParam('partnerStores'),
      styles: getParam('styles'),
      age: getRangeParam('age', filterRanges.age.min, filterRanges.age.max),
      abv: getRangeParam('abv', filterRanges.abv.min, filterRanges.abv.max),
      searchTokens: parseSearchTokens(getParam('searchTokens', false)),
      inTwlShop: getParam('inTwlShop', false) ?? '',
    },
    active,
  };
}

interface GetSearchQueryOptions {
  ignoreKeys?: Array<keyof ShopFilterParamsType>;
  includeKeys?: Array<keyof ShopFilterParamsType>;
}

function isRange(maybeRange: unknown): maybeRange is FilterRange {
  return (
    Boolean(maybeRange) &&
    {}.hasOwnProperty.call(maybeRange, 'min') &&
    {}.hasOwnProperty.call(maybeRange, 'max')
  );
}

function getKeys<T>(t: T): Array<keyof T> {
  return Object.keys(t) as Array<keyof T>;
}

export function getSearchQuery(
  href: string,
  params: ShopFilterParamsType,
  options?: GetSearchQueryOptions,
): string {
  const {searchParams} = new URL(href);

  // always remove pagination info
  searchParams.delete(PAGINATION_PARAMS.cursor.after);
  searchParams.delete(PAGINATION_PARAMS.cursor.before);

  for (const name of getKeys(params)) {
    if (options?.ignoreKeys?.includes(name)) {
      // completely ignore these keys
      continue;
    }
    if (options?.includeKeys && !options.includeKeys.includes(name)) {
      // delete these keys since they are not included
      searchParams.delete(name);
      continue;
    }

    let value = params[name];
    if (name === 'searchTokens') {
      // we have special rules for stringifying searchTokens
      value = stringifySearchTokens(value as string[]);
    }
    if (Array.isArray(value)) {
      searchParams.delete(name);
      value.forEach((v) => searchParams.append(name, v));
      continue;
    }
    if (isRange(value)) {
      const minParam = `${name}Min`;
      const maxParam = `${name}Max`;
      if (
        value.min === filterRanges[name].min &&
        value.max === filterRanges[name].max
      ) {
        // remove ranges with default values
        searchParams.delete(minParam);
        searchParams.delete(maxParam);
      } else {
        searchParams.set(minParam, value.min.toString());
        searchParams.set(maxParam, value.max.toString());
      }
    } else {
      // simple value
      if (value) {
        searchParams.set(name, value.toString());
      } else {
        searchParams.delete(name);
      }
    }
  }
  return searchParams.toString();
}

function getInt(
  maybeInt: string | null | undefined,
  defaultValue: number,
  minValue: number,
  maxValue: number,
) {
  const value = Math.max(Math.min(maxValue, parseInt(maybeInt, 10)), minValue);
  return isNaN(value) ? defaultValue : value;
}
